import React, { useState, useEffect, useCallback } from 'react';
import posthog from 'posthog-js';
import { Mail, Loader2, AlertTriangle, XCircle, Clock, User, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SharedCategoryView as SharedCategoryViewType, SharedCategoryAthlete } from '../../types';

// ── CSV export ────────────────────────────────────────────────────────────────
function escapeCsv(val: string | number | undefined | null): string {
  if (val === undefined || val === null) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(athletes: SharedCategoryAthlete[], categoryName: string) {
  const headers = [
    'Name', 'Email', 'Sport', 'Class', 'School', 'Position(s)',
    'Jersey #', 'Height', 'Weight', 'GPA', 'SAT', 'ACT',
    'Twitter', 'Instagram', 'Hudl',
    'Highlights', 'Top Stats',
  ];

  const rows = athletes.map((a) => [
    a.full_name,
    a.email ?? '',
    a.sport.replace(/_/g, ' '),
    a.grad_year,
    a.school ?? '',
    (a.positions ?? []).join(' / '),
    a.jersey_number ?? '',
    a.height ?? '',
    a.weight ?? '',
    a.gpa != null ? a.gpa.toFixed(1) : '',
    a.sat_score ?? '',
    a.act_score ?? '',
    a.social_handles?.twitter ?? '',
    a.social_handles?.instagram ?? '',
    a.hudl_link ?? '',
    (a.featured_stats ?? []).join(' | '),
    (a.top_stats ?? []).map((s) => `${s.metric}: ${s.value}`).join(' | '),
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map(escapeCsv).join(','))
    .join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${categoryName.replace(/[^a-z0-9]/gi, '_')}_ScoutLoop.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ViewState =
  | { kind: 'loading' }
  | { kind: 'email_gate'; categoryName: string }
  | { kind: 'loaded'; data: SharedCategoryViewType }
  | { kind: 'not_found' }
  | { kind: 'revoked' }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

interface SharedCategoryViewProps {
  shareToken: string;
  onClose: () => void;
}

async function fetchSharedCategory(
  shareToken: string,
  viewerEmail?: string
): Promise<
  | { ok: true; data: SharedCategoryViewType }
  | { ok: false; code: 'not_found' | 'revoked' | 'expired' | 'email_gate'; categoryName?: string }
  | { ok: false; code: 'error'; message: string }
> {
  const { data, error } = await supabase.functions.invoke('get-shared-category', {
    body: { share_token: shareToken, viewer_email: viewerEmail },
  });

  if (error) {
    return { ok: false, code: 'error', message: error.message };
  }

  if (data?.code === 'not_found') return { ok: false, code: 'not_found' };
  if (data?.code === 'revoked') return { ok: false, code: 'revoked' };
  if (data?.code === 'expired') return { ok: false, code: 'expired' };

  if (data?.require_email_to_view && !data?.email_captured) {
    return { ok: false, code: 'email_gate', categoryName: data?.category?.name };
  }

  return { ok: true, data: data as SharedCategoryViewType };
}

function AthleteCard({ athlete }: { athlete: SharedCategoryAthlete }) {
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Header row: avatar + name + jersey */}
      <div className="flex items-start gap-3">
        {athlete.avatar_url ? (
          <img
            src={athlete.avatar_url}
            alt={athlete.full_name}
            className="w-12 h-12 rounded-xl object-cover shrink-0 bg-slate-100"
          />
        ) : (
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <User size={20} className="text-slate-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-black text-slate-900 truncate">{athlete.full_name}</p>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
            {athlete.sport.replace(/_/g, ' ')} · Class of {athlete.grad_year}
          </p>
          {athlete.school && (
            <p className="text-xs font-medium text-slate-500 mt-0.5 truncate">{athlete.school}</p>
          )}
        </div>
        {athlete.jersey_number && (
          <span className="text-xs font-black text-slate-400 shrink-0">
            #{athlete.jersey_number}
          </span>
        )}
      </div>

      {/* Positions */}
      {athlete.positions && athlete.positions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {athlete.positions.map((pos) => (
            <span
              key={pos}
              className="px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 text-[9px] font-black uppercase tracking-widest"
            >
              {pos}
            </span>
          ))}
        </div>
      )}

      {/* Measurables */}
      <div className="mt-3 flex flex-wrap gap-2">
        {athlete.height && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.height}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Ht</p>
          </div>
        )}
        {athlete.weight && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.weight}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Wt</p>
          </div>
        )}
        {athlete.gpa !== undefined && athlete.gpa !== null && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.gpa.toFixed(1)}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">GPA</p>
          </div>
        )}
        {athlete.sat_score && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.sat_score}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">SAT</p>
          </div>
        )}
        {athlete.act_score && (
          <div className="bg-slate-50 rounded-xl py-1.5 px-2 text-center min-w-[48px]">
            <p className="text-xs font-black text-slate-900">{athlete.act_score}</p>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">ACT</p>
          </div>
        )}
      </div>

      {/* Highlights — resolved to "Name: Value" strings by the edge function */}
      {athlete.featured_stats && athlete.featured_stats.length > 0 && (
        <div className="mt-3">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">
            Highlights
          </p>
          <div className="flex flex-wrap gap-1">
            {athlete.featured_stats.map((s, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg text-[9px] font-bold"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Top Stats */}
      {athlete.top_stats && athlete.top_stats.length > 0 && (
        <div className="mt-3">
          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
            Top Stats
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {athlete.top_stats.map((s) => (
              <div
                key={s.metric}
                className="bg-slate-50 rounded-xl py-1.5 px-2 flex items-baseline gap-1"
              >
                <span className="text-xs font-black text-slate-900">{s.value}</span>
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest truncate">
                  {s.metric.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hudl */}
      {athlete.hudl_link && (
        <a
          href={athlete.hudl_link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-orange-500 text-white text-[9px] font-black uppercase tracking-widest hover:bg-orange-600 transition"
        >
          Watch Film on Hudl
        </a>
      )}

      {/* Contact + socials */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {athlete.email && (
          <a
            href={`mailto:${athlete.email}`}
            className="text-[9px] font-black text-amber-500 hover:text-amber-700 transition uppercase tracking-widest"
          >
            ✉ {athlete.email}
          </a>
        )}
        {athlete.social_handles?.twitter && (
          <a
            href={`https://x.com/${athlete.social_handles.twitter.replace('@', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9px] font-black text-slate-400 hover:text-slate-700 transition uppercase tracking-widest"
          >
            𝕏 {athlete.social_handles.twitter}
          </a>
        )}
        {athlete.social_handles?.instagram && (
          <a
            href={`https://instagram.com/${athlete.social_handles.instagram.replace('@', '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9px] font-black text-pink-400 hover:text-pink-600 transition uppercase tracking-widest"
          >
            IG {athlete.social_handles.instagram}
          </a>
        )}
      </div>
    </div>
  );
}

function ErrorScreen({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8 space-y-4">
      <div className="p-4 bg-slate-50 rounded-full">{icon}</div>
      <h2 className="text-xl font-black uppercase italic text-slate-900">{title}</h2>
      <p className="text-sm font-medium text-slate-500 max-w-xs">{body}</p>
    </div>
  );
}

export const SharedCategoryView: React.FC<SharedCategoryViewProps> = ({ shareToken, onClose }) => {
  const [viewState, setViewState] = useState<ViewState>({ kind: 'loading' });
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [gateLoading, setGateLoading] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);

  const load = useCallback(
    async (viewerEmail?: string) => {
      const result = await fetchSharedCategory(shareToken, viewerEmail);
      if (result.ok) {
        setViewState({ kind: 'loaded', data: result.data });
        if (!hasTracked) {
          posthog.capture('category_share_viewed', { share_token: shareToken });
          setHasTracked(true);
        }
      } else if (result.code === 'email_gate') {
        setViewState({ kind: 'email_gate', categoryName: result.categoryName ?? 'this category' });
      } else if (result.code === 'not_found') {
        setViewState({ kind: 'not_found' });
      } else if (result.code === 'revoked') {
        setViewState({ kind: 'revoked' });
      } else if (result.code === 'expired') {
        setViewState({ kind: 'expired' });
      } else if (result.code === 'error') {
        setViewState({ kind: 'error', message: result.message });
      }
    },
    [shareToken, hasTracked]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const handleEmailSubmit = async () => {
    setEmailError('');
    const trimmed = emailInput.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setGateLoading(true);
    await load(trimmed);
    setGateLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-50 z-[400] overflow-y-auto">
      {/* Nav */}
      <div className="sticky top-0 bg-white border-b border-slate-100 z-10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-amber-600 flex items-center justify-center">
            <span className="text-white text-[8px] font-black">SL</span>
          </div>
          <span className="text-xs font-black uppercase italic tracking-widest text-slate-900">
            ScoutLoop
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition"
        >
          Close
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-12">
        {/* Loading */}
        {viewState.kind === 'loading' && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 size={32} className="animate-spin text-slate-300" />
          </div>
        )}

        {/* Error states */}
        {viewState.kind === 'not_found' && (
          <ErrorScreen
            icon={<AlertTriangle size={32} className="text-slate-400" />}
            title="Link Not Found"
            body="This share link doesn't exist or may have been removed."
          />
        )}
        {viewState.kind === 'revoked' && (
          <ErrorScreen
            icon={<XCircle size={32} className="text-red-400" />}
            title="Link Revoked"
            body="The coach has revoked access to this shared list."
          />
        )}
        {viewState.kind === 'expired' && (
          <ErrorScreen
            icon={<Clock size={32} className="text-slate-400" />}
            title="Link Expired"
            body="This share link has expired. Ask the coach to send a new one."
          />
        )}
        {viewState.kind === 'error' && (
          <ErrorScreen
            icon={<AlertTriangle size={32} className="text-red-400" />}
            title="Something Went Wrong"
            body={viewState.message || 'Could not load this page. Please try again.'}
          />
        )}

        {/* Email gate */}
        {viewState.kind === 'email_gate' && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4 space-y-6">
            <div className="p-4 bg-amber-50 rounded-full">
              <Mail size={32} className="text-amber-500" />
            </div>
            <div>
              <h2 className="text-xl font-black uppercase italic text-slate-900">
                Enter your email
              </h2>
              <p className="text-sm font-medium text-slate-500 mt-2 max-w-xs">
                The coach requires an email address to view{' '}
                <span className="font-bold text-slate-700">{viewState.categoryName}</span>.
              </p>
            </div>
            <div className="w-full max-w-sm space-y-3">
              <div className="relative">
                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  size={15}
                />
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && void handleEmailSubmit()}
                  placeholder="you@school.edu"
                  className="w-full pl-10 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>
              {emailError && (
                <p className="text-red-500 text-xs font-bold text-left pl-1">{emailError}</p>
              )}
              <button
                onClick={() => void handleEmailSubmit()}
                disabled={gateLoading || !emailInput.trim()}
                className="w-full flex items-center justify-center gap-2 bg-amber-600 text-white py-4 rounded-2xl font-black uppercase italic tracking-widest text-xs hover:bg-amber-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {gateLoading ? <Loader2 size={16} className="animate-spin" /> : null}
                {gateLoading ? 'Loading…' : 'View Athletes'}
              </button>
            </div>
          </div>
        )}

        {/* Loaded view */}
        {viewState.kind === 'loaded' && (
          <div className="space-y-6 pt-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  {viewState.data.coach.full_name}
                  {viewState.data.coach.school ? ` · ${viewState.data.coach.school}` : ''}
                </p>
                <h1 className="text-2xl font-black uppercase italic text-slate-900 mt-1">
                  {viewState.data.category.name}
                </h1>
                {viewState.data.category.description && (
                  <p className="text-sm font-medium text-slate-500 mt-1">
                    {viewState.data.category.description}
                  </p>
                )}
              </div>
              {viewState.data.athletes.length > 0 && (
                <button
                  onClick={() => downloadCsv(viewState.data.athletes, viewState.data.category.name)}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-[9px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition"
                  title="Download roster as CSV (opens in Excel)"
                >
                  <Download size={12} />
                  Download CSV
                </button>
              )}
            </div>

            {/* Optional note */}
            {viewState.data.optional_message && (
              <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3">
                <p className="text-xs font-bold text-amber-800">{viewState.data.optional_message}</p>
              </div>
            )}

            {/* Athlete count */}
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              {viewState.data.athletes.length}{' '}
              {viewState.data.athletes.length === 1 ? 'Athlete' : 'Athletes'}
            </p>

            {/* Athlete grid */}
            {viewState.data.athletes.length === 0 ? (
              <p className="text-center text-sm font-bold text-slate-300 py-8">
                No athletes in this list yet.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {viewState.data.athletes.map((athlete) => (
                  <AthleteCard key={athlete.id} athlete={athlete} />
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="text-center pt-4">
              <p className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">
                Powered by ScoutLoop · No account required
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
